/**
 * Todos los datos del inmueble, en una ficha.
 *
 * El alta pide muchos datos —planta, puerta, referencia catastral, superficie, año, baños,
 * certificado energético, costes de compra…— y la ficha del inmueble solo enseñaba una parte:
 * lo demás se guardaba y no había forma de verlo sin abrir «Editar». Esto lo enseña todo, de
 * solo lectura, desde el ojo que va junto al nombre.
 *
 * Es un diálogo como el visor de documentos: Esc cierra, el foco entra y vuelve al ojo, y el
 * fondo no se desplaza.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { PropertyDetailResponse } from '@naar/shared';
import { Icon, type IconName } from '../../components/Icon';
import { formatEur, formatNumber, formatShortDate } from '../../lib/format';
import { sumMoney } from '../../lib/moneyInput';
import styles from './PropertyInfoCard.module.css';

const TIPOS: Record<string, string> = {
  piso: 'Piso',
  casa: 'Casa',
  local: 'Local',
  edificio: 'Edificio',
  habitaciones: 'Alquiler por habitaciones',
};

const ESTADOS: Record<string, string> = {
  activa: 'Activa',
  en_reforma: 'En reforma',
  vendida: 'Vendida',
};

const PRECISION: Record<string, string> = {
  exacta: 'portal exacto',
  calle: 'calle, sin número',
  municipio: 'solo el municipio',
  manual: 'colocada a mano',
  sin_geocodificar: 'sin ubicar',
};

/** El ojo junto al nombre del inmueble. */
export function PropertyInfoButton({ property }: { property: PropertyDetailResponse }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={styles.ojo}
        onClick={() => setOpen(true)}
        aria-label="Ver todos los datos del inmueble"
        title="Ver todos los datos"
      >
        <Icon name="eye" size={18} strokeWidth={2} />
      </button>
      {open && <PropertyInfoCard property={property} onClose={() => setOpen(false)} />}
    </>
  );
}

function PropertyInfoCard({ property: p, onClose }: { property: PropertyDetailResponse; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement;
    dialogRef.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [onClose]);

  const costes = p.acquisitionCostItems ?? [];
  const totalCostes = costes.length > 0 ? sumMoney(costes.map((item) => item.amount)) : p.acquisitionCosts;
  const inversion = sumMoney([p.acquisitionPrice, totalCostes]);
  const coordenadas =
    p.latitude !== null && p.longitude !== null ? `${p.latitude.toFixed(5)}, ${p.longitude.toFixed(5)}` : null;
  const plantaPuerta = [p.floor && `${p.floor}º`, p.door].filter(Boolean).join(' ');
  const direccion = [p.addressLine, plantaPuerta].filter(Boolean).join(', ');
  const lugar = [[p.postalCode, p.city].filter(Boolean).join(' '), p.province].filter(Boolean).join(', ');

  return createPortal(
    <div className={styles.capa} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ficha-inmueble-titulo"
        tabIndex={-1}
      >
        <header className={styles.cabecera}>
          <div className={styles.titulo}>
            <span className={styles.eyebrow}>Datos del inmueble</span>
            <h2 id="ficha-inmueble-titulo" className={styles.nombre}>
              {p.name}
            </h2>
            <span className={styles.direccion}>
              <Icon name="pin" size={14} />
              {[direccion, lugar].filter(Boolean).join(' · ')}
            </span>
            <span className={styles.etiquetas}>
              <span className={styles.etiqueta}>{TIPOS[p.propertyType] ?? p.propertyType}</span>
              <span className={`${styles.etiqueta} ${p.status === 'activa' ? styles.etiquetaOk : ''}`}>
                {ESTADOS[p.status] ?? p.status}
              </span>
              {p.energyRating && <span className={styles.etiqueta}>CEE {p.energyRating}</span>}
              {p.financingEnabled && <span className={styles.etiqueta}>Con financiación</span>}
            </span>
          </div>
          <button type="button" className={styles.cerrar} onClick={onClose} aria-label="Cerrar" title="Cerrar (Esc)">
            <Icon name="close" size={18} strokeWidth={2} />
          </button>
        </header>

        <div className={styles.cuerpo}>
          {/* Las cuatro cifras que se buscan primero. */}
          <div className={styles.cifras}>
            <Cifra etiqueta="Superficie" valor={p.areaM2 !== null ? `${formatNumber(p.areaM2)} m²` : null} />
            <Cifra etiqueta="Habitaciones" valor={String(p.units.length)} />
            <Cifra etiqueta="Inversión total" valor={formatEur(inversion)} />
            <Cifra etiqueta="Valor actual" valor={formatEur(p.valuation.current)} />
          </div>

          <div className={styles.bloques}>
            <Bloque titulo="El inmueble" icono="building">
              <Fila etiqueta="Tipo" valor={TIPOS[p.propertyType] ?? p.propertyType} />
              <Fila etiqueta="Año de construcción" valor={p.builtYear} />
              <Fila etiqueta="Dormitorios" valor={p.bedrooms} />
              <Fila etiqueta="Baños" valor={p.bathrooms} />
              <Fila etiqueta="Habitaciones en alquiler" valor={p.units.length} />
              <Fila
                etiqueta="Certificado energético"
                valor={
                  p.energyRating
                    ? `${p.energyRating}${p.energyRatingExpiresAt ? ` · hasta ${formatShortDate(p.energyRatingExpiresAt)}` : ''}`
                    : null
                }
              />
            </Bloque>

            <Bloque titulo="Ubicación" icono="pin">
              <Fila etiqueta="Dirección" valor={p.addressLine} />
              <Fila etiqueta="Planta y puerta" valor={plantaPuerta || null} />
              <Fila etiqueta="Código postal" valor={p.postalCode} />
              <Fila etiqueta="Ciudad" valor={p.city} />
              <Fila etiqueta="Provincia" valor={p.province} />
              <Fila etiqueta="Ref. catastral" valor={p.cadastralReference} mono />
              <Fila
                etiqueta="En el mapa"
                valor={coordenadas ? `${PRECISION[p.geocodePrecision ?? ''] ?? 'ubicado'} · ${coordenadas}` : 'Sin ubicar'}
                tenue={!coordenadas}
              />
            </Bloque>

            <Bloque titulo="Compra" icono="euro">
              <Fila etiqueta="Fecha" valor={p.acquisitionDate ? formatShortDate(p.acquisitionDate) : null} />
              <Fila etiqueta="Precio de compra" valor={formatEur(p.acquisitionPrice)} />
              {costes.length > 0 ? (
                costes.map((item, index) => (
                  <Fila key={`${item.concept}-${index}`} etiqueta={item.concept} valor={formatEur(item.amount)} sangrado />
                ))
              ) : (
                <Fila etiqueta="Costes de compra" valor={formatEur(totalCostes)} />
              )}
              {costes.length > 0 && <Fila etiqueta="Total costes" valor={formatEur(totalCostes)} />}
              <Fila etiqueta="Inversión total" valor={formatEur(inversion)} total />
            </Bloque>

            <Bloque titulo="Otros" icono="info">
              <Fila etiqueta="Valor de mercado" valor={formatEur(p.valuation.current)} />
              <Fila etiqueta="Financiación" valor={p.financingEnabled ? 'Activada' : 'No'} />
              {p.updatedAt && <Fila etiqueta="Última modificación" valor={formatShortDate(p.updatedAt.slice(0, 10))} />}
              <div className={styles.notas}>
                <span className={styles.notasEtiqueta}>Notas</span>
                {p.notes ? <p className={styles.notasTexto}>{p.notes}</p> : <span className={styles.vacio}>Sin notas</span>}
              </div>
            </Bloque>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Cifra({ etiqueta, valor }: { etiqueta: string; valor: string | null }) {
  return (
    <div className={styles.cifra}>
      <span className={styles.cifraEtiqueta}>{etiqueta}</span>
      <span className={valor ? styles.cifraValor : `${styles.cifraValor} ${styles.vacio}`}>{valor ?? '—'}</span>
    </div>
  );
}

function Bloque({ titulo, icono, children }: { titulo: string; icono: IconName; children: ReactNode }) {
  return (
    <section className={styles.bloque}>
      <h3 className={styles.bloqueTitulo}>
        <Icon name={icono} size={15} />
        {titulo}
      </h3>
      <dl className={styles.filas}>{children}</dl>
    </section>
  );
}

function Fila({
  etiqueta,
  valor,
  mono = false,
  total = false,
  sangrado = false,
  tenue = false,
}: {
  etiqueta: string;
  valor: ReactNode;
  mono?: boolean;
  total?: boolean;
  sangrado?: boolean;
  tenue?: boolean;
}) {
  const vacio = valor === null || valor === undefined || valor === '';
  return (
    <div className={[styles.fila, total ? styles.filaTotal : '', sangrado ? styles.filaSangrada : ''].filter(Boolean).join(' ')}>
      <dt>{etiqueta}</dt>
      <dd className={[vacio || tenue ? styles.vacio : '', mono && !vacio ? styles.mono : ''].filter(Boolean).join(' ') || undefined}>
        {vacio ? '—' : valor}
      </dd>
    </div>
  );
}
